import { useEffect, useState } from "react";
import { roleAPI, permissionAPI } from "../services/api";
import { Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdSearch, MdSettings, MdHourglassEmpty } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array(cols).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600, margin:"14px 0 8px",
                borderBottom:`1px solid ${BORDER}`, paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function RolesTab() {
  const { t } = useLanguage();
  const [list,        setList]        = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [modal,       setModal]       = useState(null);
  const [addPermSearch, setAddPermSearch] = useState("");
  const [addSelPerms,   setAddSelPerms]   = useState([]);
  const [form,        setForm]        = useState({});
  const [selected,    setSelected]    = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [rolePerms,   setRolePerms]   = useState([]);
  const [selPerms,    setSelPerms]    = useState([]);
  const [loadingPerms,setLoadingPerms]= useState(false);
  const [permSearch,  setPermSearch]  = useState("");
  const [toast,       setToast]       = useState(null);
  const [err,         setErr]       = useState(null);
  const [deleteId,    setDeleteId]    = useState(null);
  const [page,        setPage]        = useState(0);
  const [pageSize,    setPageSize]    = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await roleAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    permissionAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setPermissions(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const displayName = (form.displayName || "").trim();
    if (!displayName) {
      setErr(t("សូមបញ្ចូលឈ្មោះតួនាទី"));
      return;
    }
    setSaving(true);
    try {
      const roleName = (form.roleName || "").trim()
        || displayName.toUpperCase().replace(/\s+/g, "_").slice(0, 100);
      const payload = {
        roleName,
        displayName,
        description:   form.description  || "",
        permissionIds: addSelPerms.map(Number),
      };
      if (editId) {
        await roleAPI.update(editId, payload);
      } else {
        await roleAPI.create(payload);
      }
      await load();
      setToast(editId ? "កែប្រែតួនាទីបានជោគជ័យ!" : "បន្ថែមតួនាទីបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null); setAddSelPerms([]);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (r) => {
    setEditId(r.roleId ?? r.id);
    setForm({
      roleName:    r.roleName,
      displayName: r.displayName,
      description: r.description,
    });
    setAddPermSearch("");
    try {
      const res  = await roleAPI.getById(r.roleId ?? r.id);
      const data = res?.data?.data ?? res?.data;
      const assignedNames = Array.isArray(data?.permissions) ? data.permissions : [];
      const matchedIds = permissions
        .filter(p => assignedNames.includes(p.permissionName))
        .map(p => Number(p.permissionId ?? p.id));
      setAddSelPerms(matchedIds);
    } catch { setAddSelPerms([]); }
    setModal("add");
  };

  const openPermissions = async (role) => {
    setSelected(role);
    setSelPerms([]);
    setRolePerms([]);
    setPermSearch("");
    setModal("perm");
    setLoadingPerms(true);
    try {
      const roleRes  = await roleAPI.getById(role.roleId ?? role.id);
      const roleData = roleRes?.data?.data ?? roleRes?.data;
      const assignedNames = Array.isArray(roleData?.permissions)
        ? roleData.permissions : [];

      const matchedIds = permissions
        .filter(p => assignedNames.includes(p.permissionName))
        .map(p => Number(p.permissionId ?? p.id));

      setRolePerms(assignedNames);
      setSelPerms(matchedIds);
    } catch (e) {
      console.error("Permission load error:", e);
      setRolePerms([]); setSelPerms([]);
    } finally {
      setLoadingPerms(false);
    }
  };

  const handleSavePerms = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await roleAPI.update(selected.roleId ?? selected.id, {
        roleName:      selected.roleName    || "",
        displayName:   selected.displayName || "",
        description:   selected.description || "",
        permissionIds: selPerms.map(Number),
      });
      setToast(t("រក្សាទុកសិទ្ធិបានជោគជ័យ!"));
      setModal(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await roleAPI.delete(id);
      await load();
      setToast(t("លុបតួនាទីបានជោគជ័យ!"));
    } catch (e) {
      setErr(errMessage(e));
    }
  };

  const togglePerm = (id) => {
    const nid = Number(id);
    setSelPerms(prev =>
      prev.map(Number).includes(nid)
        ? prev.filter(x => Number(x) !== nid)
        : [...prev, nid]
    );
  };

  const filtered = list.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.roleName, r.displayName, r.description]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  useEffect(() => { setPage(0); }, [search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const modules = [...new Set(permissions.map(p => p.module).filter(Boolean))];
  const filteredPerms = permissions.filter(p =>
    !permSearch || (p.permissionName || "").toLowerCase().includes(permSearch.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:16 }}>
        <div>
          <h3 style={{ fontSize:16, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:2 }}>{t("តួនាទី")}</h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("ចំនួនតួនាទី:")} <strong style={{ color:BROWN }}>{list.length}</strong>
          </p>
        </div>
        <Can perm="role" action="create">
        <button onClick={() => { setEditId(null); setForm({}); setAddSelPerms([]); setAddPermSearch(""); setModal("add"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, fontFamily:KH }}>
          {t("+ បន្ថែមតួនាទីថ្មី")}
        </button>
        </Can>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:8,
                    background:"var(--surface)", border:`1px solid ${BORDER}`,
                    borderRadius:8, padding:"0 12px", marginBottom:16 }}>
        <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("ស្វែងរក....")}
          style={{ border:"none", outline:"none", background:"transparent",
                   flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ល.រ")}</Th>
              <Th>{t("ឈ្មោះតួនាទី")}</Th>
              <Th>{t("ស្លាកឈ្មោះ")}</Th>
              <Th>{t("ការពិពណ៌នា")}</Th>
              <Th>{t("សិទ្ធិ")}</Th>
              <ThActions perm="role" actions={["edit","delete"]} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3].map(i => <SkeletonRow key={i} cols={6} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានតួនាទី")}
                </td>
              </tr>
            ) : pageItems.map((r, idx) => (
              <tr key={r.roleId ?? r.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{page * pageSize + idx + 1}</Td>
                <Td>
                  <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                                 fontWeight:600, background:"#EDE9FE", color:"#5B21B6",
                                 fontFamily:KH }}>
                    {r.roleName || "—"}
                  </span>
                </Td>
                <Td>{r.displayName || "—"}</Td>
                <Td muted>{r.description || "—"}</Td>
                <Td>
                  <button onClick={() => openPermissions(r)}
                    style={{ padding:"4px 12px", borderRadius:6,
                             border:`1px solid ${BORDER}`,
                             background:"var(--surface)", cursor:"pointer",
                             fontSize:12, color:BROWN, fontFamily:KH,
                             display:"inline-flex", alignItems:"center", gap:5 }}>
                    <MdSettings size={14}/> {t("សិទ្ធិ")}
                  </button>
                </Td>
                <ActionBtns perm="role"
                  onEdit={() => openEdit(r)}
                  onDelete={() => setDeleteId(r.roleId ?? r.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបតួនាទីនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          key={editId || "new"}
          title={editId ? "កែប្រែតួនាទី" : "បន្ថែមតួនាទីអ្នកប្រើប្រាស់"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); }}
        >
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("ឈ្មោះតួនាទី")} <span style={{color:"#EF4444"}}>*</span>
            </label>
            <input value={form.displayName||""}
              onChange={e => f("displayName", e.target.value)}
              placeholder={t("បញ្ចូលឈ្មោះតួនាទី")}
              style={inputStyle} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>{t("ការពិពណ៌នា")}</label>
            <textarea value={form.description||""}
              onChange={e => f("description", e.target.value)}
              rows={3} placeholder={t("បញ្ចូលការពិពណ៌នា")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:6, fontFamily:KH }}>
              សិទ្ធិ
              {addSelPerms.length > 0 && (
                <span style={{ color:BROWN, marginLeft:8 }}>
                  ({addSelPerms.length} បានជ្រើស)
                </span>
              )}
            </label>
            <div style={{ display:"flex", alignItems:"center", gap:8,
                          background:BG, border:`1px solid ${BORDER}`,
                          borderRadius:8, padding:"0 10px", marginBottom:8 }}>
              <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
              <input value={addPermSearch}
                onChange={e => setAddPermSearch(e.target.value)}
                placeholder={t("ស្វែងរកសិទ្ធិ...")}
                style={{ border:"none", outline:"none", background:"transparent",
                         flex:1, padding:"7px 0", fontSize:12, fontFamily:KH }} />
            </div>
            <div style={{ maxHeight:220, overflowY:"auto",
                          border:`1px solid ${BORDER}`, borderRadius:8 }}>
              {(() => {
                const filtered = permissions.filter(p =>
                  !addPermSearch ||
                  (p.permissionName||"").toLowerCase().includes(addPermSearch.toLowerCase())
                );
                const mods = [...new Set(filtered.map(p => p.module).filter(Boolean))];
                return mods.length > 0 ? mods.map(mod => {
                  const modPerms = filtered.filter(p => p.module === mod);
                  return (
                    <div key={mod}>
                      <div style={{ padding:"6px 12px", background:"var(--border)",
                                    fontSize:10, fontWeight:700, color:BROWN,
                                    letterSpacing:0.5, fontFamily:KH }}>
                        {mod}
                      </div>
                      {modPerms.map(p => {
                        const pid = Number(p.permissionId ?? p.id);
                        const checked = addSelPerms.map(Number).includes(pid);
                        return (
                          <label key={pid}
                            style={{ display:"flex", alignItems:"center", gap:10,
                                     padding:"8px 12px", cursor:"pointer",
                                     borderBottom:`1px solid ${BORDER}`,
                                     background: checked ? BG : "var(--surface)" }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => setAddSelPerms(prev =>
                                prev.map(Number).includes(pid)
                                  ? prev.filter(x => Number(x) !== pid)
                                  : [...prev, pid]
                              )}
                              style={{ width:15, height:15, accentColor:BROWN }} />
                            <span style={{ fontSize:12, fontFamily:"monospace",
                                           color:"var(--text)" }}>
                              {p.permissionName}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                }) : (
                  <p style={{ padding:"16px", textAlign:"center",
                              color:MUTED, fontSize:12, fontFamily:KH }}>
                    {permissions.length === 0 ? "កំពុងផ្ទុក..." : "គ្មានលទ្ធផល"}
                  </p>
                );
              })()}
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "perm" && selected && (
        <Modal
          title={t("បន្ថែមតួនាទីអ្នកប្រើប្រាស់")}
          onClose={() => { setModal(null); setSelected(null); }}
        >
          <p style={{ fontSize:12, color:MUTED, marginBottom:12, fontFamily:KH }}>
            {t("តួនាទី:")} <strong style={{ color:BROWN }}>
              {selected.displayName || selected.roleName}
            </strong>
          </p>

          <div style={{ display:"flex", alignItems:"center", gap:8,
                        background:BG, border:`1px solid ${BORDER}`,
                        borderRadius:8, padding:"0 12px", marginBottom:12 }}>
            <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
            <input value={permSearch} onChange={e => setPermSearch(e.target.value)}
              placeholder={t("ស្វែករក.....")}
              style={{ border:"none", outline:"none", background:"transparent",
                       flex:1, padding:"8px 0", fontSize:13, fontFamily:KH }} />
          </div>

          {loadingPerms && (
            <div style={{ padding:"12px 16px", background:BG,
                          borderRadius:8, marginBottom:8,
                          fontSize:12, color:MUTED, fontFamily:KH,
                          display:"flex", alignItems:"center", gap:6 }}>
              <MdHourglassEmpty size={14}/> {t("កំពុងផ្ទុកសិទ្ធិដែលមានហើយ...")}
            </div>
          )}
          <div style={{ maxHeight:280, overflowY:"auto",
                        border:`1px solid ${BORDER}`, borderRadius:8,
                        marginBottom:14 }}>
            {modules.length > 0 ? modules.map(mod => {
              const modPerms = filteredPerms.filter(p => p.module === mod);
              if (modPerms.length === 0) return null;
              return (
                <div key={mod}>
                  <div style={{ padding:"8px 14px", background:"var(--border)",
                                fontSize:11, fontWeight:700, color:BROWN,
                                fontFamily:KH, letterSpacing:0.5 }}>
                    {mod}
                  </div>
                  {modPerms.map(p => {
                    const pid = p.permissionId ?? p.id;
                    return (
                      <label key={pid}
                        style={{ display:"flex", alignItems:"center", gap:12,
                                 padding:"10px 14px", cursor:"pointer",
                                 borderBottom:`1px solid ${BORDER}`,
                                 background: selPerms.map(Number).includes(Number(pid)) ? BG : "var(--surface)" }}>
                        <input type="checkbox"
                          checked={selPerms.map(Number).includes(Number(pid))}
                          onChange={() => togglePerm(pid)}
                          style={{ width:16, height:16, accentColor:BROWN,
                                   cursor:"pointer", flexShrink:0 }} />
                        <div>
                          <p style={{ fontSize:13, fontWeight:500, color:"var(--text)",
                                       margin:0, fontFamily:KH }}>
                            {p.permissionName}
                          </p>
                          {p.description && (
                            <p style={{ fontSize:11, color:MUTED, margin:0,
                                         fontFamily:KH }}>
                              {p.description}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            }) : (
              filteredPerms.map(p => {
                const pid = p.permissionId ?? p.id;
                return (
                  <label key={pid}
                    style={{ display:"flex", alignItems:"center", gap:12,
                             padding:"10px 14px", cursor:"pointer",
                             borderBottom:`1px solid ${BORDER}`,
                             background: selPerms.map(Number).includes(Number(pid)) ? BG : "var(--surface)" }}>
                    <input type="checkbox"
                      checked={selPerms.map(Number).includes(Number(pid))}
                      onChange={() => togglePerm(pid)}
                      style={{ width:16, height:16, accentColor:BROWN,
                               cursor:"pointer", flexShrink:0 }} />
                    <div>
                      <p style={{ fontSize:13, fontWeight:500, color:"var(--text)",
                                   margin:0, fontFamily:KH }}>
                        {p.permissionName}
                      </p>
                    </div>
                  </label>
                );
              })
            )}
            {filteredPerms.length === 0 && (
              <p style={{ padding:"20px", textAlign:"center",
                          color:MUTED, fontSize:12, fontFamily:KH }}>
                {t("គ្មានសិទ្ធិ")}
              </p>
            )}
          </div>

          {selPerms.length > 0 && (
            <p style={{ fontSize:12, color:BROWN, marginBottom:10, fontFamily:KH }}>
              បានជ្រើស {selPerms.length} សិទ្ធិ
            </p>
          )}

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setSelected(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSavePerms} style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "អញ្ជូរចុក"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PermissionsTab() {
  const { t } = useLanguage();
  const [list,      setList]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [modFilter, setModFilter] = useState("");
  const [page,      setPage]      = useState(0);
  const [pageSize,  setPageSize]  = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await permissionAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const modules = [...new Set(list.map(p => p.module).filter(Boolean))];

  const filtered = list.filter(p => {
    const matchSearch  = !search    || (p.permissionName || "").toLowerCase().includes(search.toLowerCase());
    const matchModule  = !modFilter || p.module === modFilter;
    return matchSearch && matchModule;
  });

  useEffect(() => { setPage(0); }, [search, modFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:16 }}>
        <div>
          <h3 style={{ fontSize:16, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:2 }}>{t("សិទ្ធិ")}</h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("ចំនួនសិទ្ធិ:")} <strong style={{ color:BROWN }}>{list.length}</strong>
          </p>
        </div>
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកសិទ្ធិ....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <select value={modFilter} onChange={e => setModFilter(e.target.value)}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ក្រុមទាំងអស់")}</option>
          {modules.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ល.រ")}</Th>
              <Th>{t("ឈ្មោះសិទ្ធិ")}</Th>
              <Th>{t("ក្រុម")}</Th>
              <Th>{t("ការពិពណ៌នា")}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={4} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានសិទ្ធិ")}
                </td>
              </tr>
            ) : pageItems.map((p, idx) => (
              <tr key={p.permissionId ?? p.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{page * pageSize + idx + 1}</Td>
                <Td>
                  <span style={{ fontFamily:"monospace", fontSize:12,
                                 background:"#F3F4F6", padding:"2px 8px",
                                 borderRadius:4 }}>
                    {p.permissionName}
                  </span>
                </Td>
                <Td>
                  <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                                 fontWeight:600, background:"#DBEAFE", color:"#1E40AF",
                                 fontFamily:KH }}>
                    {p.module || "—"}
                  </span>
                </Td>
                <Td muted>{p.description || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

    </div>
  );
}

export default function Roles({ defaultTab = "roles" }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:20,
                    borderBottom:`1px solid ${BORDER}`, paddingBottom:12 }}>
        {[
          { key:"roles",       label:"តួនាទី" },
          { key:"permissions", label:"សិទ្ធិ"  },
        ].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)} style={{
            padding:"9px 22px", borderRadius:8, cursor:"pointer",
            fontSize:13, fontWeight:600, fontFamily:KH, transition:"all 0.15s",
            background: tab===tb.key ? BROWN  : "var(--surface)",
            color:      tab===tb.key ? "#fff" : "var(--text)",
            border:     tab===tb.key ? "none" : `1px solid ${BORDER}`,
            boxShadow:  tab===tb.key ? "0 2px 8px rgba(92,45,14,0.3)" : "none",
          }}>
            {t(tb.label)}
          </button>
        ))}
      </div>

      {tab === "roles"       && <RolesTab />}
      {tab === "permissions" && <PermissionsTab />}
    </div>
  );
}